import { ThemeColor } from '@/enums'
import { useSegment } from '@/modules'
import { autoRecommendationCategories } from '@/pages/RequestOffer/helpers'
import { useSnackbar } from '@/providers'
import { paths } from '@/routes/paths'
import { useLocationHome, useRequestRecommendation } from '@/services'
import { theme } from '@/theme'
import { HomeRecommendation } from '@/types/location'
import { useGoToPath, useItemDialog, useSearchParams } from '@/utils'
import { useCategoryTranslations } from '@/utils/hooks/useCategoryTranslations'
import { Button } from '@surein/ui'
import { Check, Upload } from 'lucide-react'
import { allPass, prop, propEq } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  CategoryIcon,
  FilePicker,
  Loader,
  Row,
  ShowWhen,
  Typography,
} from '../../../components'
import {
  LocationData,
  checkIfRecommendationExists,
} from '../../LocationPage/helpers'
import { UploadContractModal } from '../UploadContractModal'
import {
  DefaultInsuranceDetails,
  HeaderDetails,
  InsuranceDefaultViewWrapper,
  PageActions,
  UspsContainer,
} from '../styles'

interface RecommendationActionButtonProps {
  doesRecommendationExist: boolean
  isLoading: boolean
  handleRequestOffer: () => void
  goToRecommendation: (id: string) => void
  recommendationId?: string
  sentRecommendationForCategory: HomeRecommendation | undefined
}

const RecommendationActionButton = ({
  doesRecommendationExist = false,
  isLoading,
  handleRequestOffer,
  goToRecommendation,
  recommendationId,
  sentRecommendationForCategory,
}: RecommendationActionButtonProps) => {
  const { t } = useTranslation()
  if (!doesRecommendationExist) {
    return (
      <Button
        variant="primary"
        shape="square"
        loading={isLoading}
        onClick={handleRequestOffer}
      >
        {t('requestOffer')}
      </Button>
    )
  }

  if (sentRecommendationForCategory) {
    return (
      <Button
        variant="primary"
        shape="square"
        onClick={() =>
          goToRecommendation(
            recommendationId || sentRecommendationForCategory.recommendationId,
          )
        }
      >
        {t('openOffer')}
      </Button>
    )
  }

  return (
    <Button shape="square" disabled>
      {t('offerInProgress')}
    </Button>
  )
}

export const InsuranceDefaultView = () => {
  const { recommendationId } = useSearchParams()
  const { categoryId, locationId = '' } = useParams<{
    categoryId: string
    locationId: string
  }>()
  const { t } = useTranslation()
  const snackbars = useSnackbar()
  const { data, isLoading } = useLocationHome({ id: locationId })
  const categoryTranslations = useCategoryTranslations(categoryId as string)
  const requestOfferMutation = useRequestRecommendation(locationId as string)
  const {
    item: files,
    openItemDialog,
    isOpen,
    closeItemDialog,
  } = useItemDialog<File[]>([])
  const goToLocation = useGoToPath(
    paths.viewLocationWithKey(locationId as string),
  )

  const goToRecommendation = useGoToPath(paths.viewRecommendationWithKey)
  const offerPath = paths.requestOfferWithKey({
    locationId: locationId || '',
    categoryId: categoryId || '',
  })
  const goToRequestOffer = useGoToPath(offerPath)

  const analytics = useSegment()

  const handleRequestOffer = async () => {
    await analytics.track('app_request_offer', {
      categoryId,
      source: 'insurance_finder',
    })

    if (autoRecommendationCategories.includes(categoryId as string)) {
      goToRequestOffer()
      return
    }

    try {
      await requestOfferMutation.mutateAsync({
        categoryId: categoryId as string,
      })
      await analytics.track('app_request_offer_failed', {
        categoryId,
      })
      snackbars.success(t('snackbars.success.recommendationRequestSent'))
      goToLocation()
    } catch (error) {
      console.error(error)
      snackbars.error(t('snackbars.error.generalMsg'))
    }
  }

  if (isLoading || !data) return <Loader />

  const sentRecommendationForCategory = data.recommendations?.find(
    allPass([
      prop('sended'),
      prop('lowestPrice'),
      propEq(categoryId as string, 'categoryId'),
    ]),
  )

  return (
    <InsuranceDefaultViewWrapper>
      <DefaultInsuranceDetails>
        <HeaderDetails>
          <Row alignItems="center" gap="8px">
            <CategoryIcon categoryId={categoryId as string} />
            <Typography responsive variant="h4">
              {categoryTranslations.categoryName}
            </Typography>
          </Row>
          <Typography color={ThemeColor.b50} variant="p1Body">
            {categoryTranslations.longDesc}
          </Typography>
        </HeaderDetails>
        <ShowWhen when={categoryTranslations.usps.length > 0}>
          <UspsContainer>
            {categoryTranslations.usps.map((item: string) => (
              <Row key={item} alignItems="center" gap="16px">
                <Check color={theme.color.info1} />
                <Typography variant="p1Body">{item}</Typography>
              </Row>
            ))}
          </UspsContainer>
        </ShowWhen>
        <PageActions>
          <FilePicker accept=".pdf,.jpg" onPick={openItemDialog}>
            <Button variant="muted" shape="square" suffixIcon={Upload}>
              {t('uploadExistingContract')}
            </Button>
          </FilePicker>
          <RecommendationActionButton
            doesRecommendationExist={checkIfRecommendationExists(
              data as LocationData,
              categoryId,
            )}
            isLoading={requestOfferMutation.isLoading}
            handleRequestOffer={handleRequestOffer}
            goToRecommendation={goToRecommendation}
            recommendationId={recommendationId}
            sentRecommendationForCategory={
              sentRecommendationForCategory as HomeRecommendation
            }
          />
        </PageActions>
      </DefaultInsuranceDetails>
      {isOpen && (
        <UploadContractModal files={files} onClose={closeItemDialog} />
      )}
    </InsuranceDefaultViewWrapper>
  )
}
